import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { LaunchError } from "../../utils/errors";
import { NewFeaturesContext } from "./newFeaturesContext";
import { isKnownNewFeatureId } from "./catalog";
import {
  isFeatureSeen,
  subscribeNewFeaturesSeen,
  writeNewFeatureSeen,
} from "./seenRepository";

const NewFeaturesProvider = ({ children }) => {
  const dispatch = useDispatchApp();
  const user = useSelectorApp((state) => state.auth.user);
  const uid = user?.uid;

  const [seenById, setSeenById] = useState({});
  const [isSeenReady, setIsSeenReady] = useState(false);
  const seenByIdRef = useRef(seenById);

  seenByIdRef.current = seenById;

  const markSeen = useCallback(
    (featureId) => {
      if (!featureId || !uid || !isKnownNewFeatureId(featureId)) {
        return;
      }

      if (isFeatureSeen(featureId, seenByIdRef.current)) {
        return;
      }

      setSeenById((previous) => {
        if (isFeatureSeen(featureId, previous)) {
          return previous;
        }

        return {
          ...previous,
          [featureId]: true,
        };
      });

      posthog.capture("new_feature_seen", { feature_id: featureId });

      writeNewFeatureSeen(uid, featureId).catch((error) => {
        dispatch(
          LaunchError(error, false, [], {
            functionName: "writeNewFeatureSeen",
            context: { featureId },
            tags: { action: "newFeaturesMarkSeen" },
            level: "error",
          })
        );
      });
    },
    [dispatch, uid]
  );

  useEffect(() => {
    if (!uid) {
      setSeenById({});
      setIsSeenReady(true);
      return undefined;
    }

    setIsSeenReady(false);

    const unsubscribe = subscribeNewFeaturesSeen(
      uid,
      (nextSeenById) => {
        setSeenById(nextSeenById);
        setIsSeenReady(true);
      },
      (error) => {
        setIsSeenReady(true);
        dispatch(
          LaunchError(error, false, [], {
            functionName: "subscribeNewFeaturesSeen",
            context: { uid },
            tags: { action: "newFeaturesListener" },
            level: "error",
          })
        );
      }
    );

    return unsubscribe;
  }, [dispatch, uid]);

  const value = useMemo(
    () => ({
      uid,
      seenById,
      isSeenReady,
      markSeen,
    }),
    [isSeenReady, markSeen, seenById, uid]
  );

  return (
    <NewFeaturesContext.Provider value={value}>
      {children}
    </NewFeaturesContext.Provider>
  );
};

export default NewFeaturesProvider;
